import { useEffect, useMemo, useState } from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import useEditorService from "@/hooks/useEditorService";
import Layer from "local_modules/editor/src/Layer";
import UploadAssetPrompt from "./UploadAssetPrompt";
import PromptGenerator from "./PromptGenerator";
import AIBackgroundResults from "./AIBackgroundsResults";
import ImageLayer from "local_modules/editor/lib/ImageLayer";
import { generateAttributesAndPrompt} from "../../services/AIBackgrounds";
import RemoveBackgroundAndApply from "./RemoveBackgroundAndApply";
import { 
    aiBackgroundPromptAtom, 
    aiBackgroundUrlsAtoms, 
    aiBackgroundsS3UrlsAtom,
    materialAtom,
    settingsAtom,
    productAtom,
    lightingAtom,
    moodAtom,
    scaleAndProportionAtom,
    colorPalleteAtom,
    additionalElementsAtom,
    materialListAtom,
    settingsListAtom,
    productListAtom,
    lightingListAtom,
    moodListAtom,
    scaleAndProportionListAtom,
    colorPalleteListAtom,
    additionalElementsListAtom,
    aigenerationLayerProgressAtom,
    attributeListAtom,
    activeProjectAtom,
    activePageAtom,
    pageStatesAtom,
    pagesAtom,
    selectedLayerAtom
 } from "../../Atoms";
import GlobalAPI from '@/modules/batchEditor/services/GlobalAPI';
import useToast from "@/hooks/useToast";
import DMSCanvas from "local_modules/editor/src/DMSCanvas";
import useResetAtomHandler from "@/modules/editor-v2/resetAtomHandler";
import { TrackWebappEvent } from "@/modules/segment-event-tracking/segmentEventTracking.js";
import { EventTrackName } from "@/modules/segment-event-tracking/constant.js";
import {FormattedTimestamp} from "@/hooks/FormattedTimestamp";
import { generateAIImages } from "./useAIgenerationEditor";
import {decodeBase64ToBlob, needToRemoveBG} from 'src/utils/helpers';

const pattern = /(?<subcategory>[\w\s]+) on (?<material>[\w\s]+) (?<setting>[\w\s]+) in a (?<environment>[\w\s]+) in a (?<style>[\w\s]+) style next to (?<prop>\b\w+\b(?:\s+\b\w+\b)?)$/;

export default function AIBackgrounds({setIsAIBackgrounds, setAttributePage, attributePage, getSegmentPayloadAtom, activeProject}) {

    const pagesStates = useAtomValue(pageStatesAtom);
    const pages = useAtomValue(pagesAtom);
    const activePage = useAtomValue(activePageAtom);
    const [layers,setLayers] = useState<Layer[]>([])
    const {activeEditor} = useEditorService();
    const [generating, setGenerating] = useState(false);
    const [urls, setUrls] = useAtom(aiBackgroundUrlsAtoms);
    const [prompt, setPrompt] = useAtom(aiBackgroundPromptAtom);
    const [s3Url, setS3Url] = useState("");
    const [assisted, setAssisted] = useState(true);
    const [base64Url, setBase64] = useState();
    const [promptGenerating, setPromptGenerating] = useState(false);
    const [attributeList, setAttributeList] = useAtom(attributeListAtom)
    const [material, setMaterial] = useAtom(materialAtom);
    const [settings, setSettings] = useAtom(settingsAtom);
    const [product, setProduct] = useAtom(productAtom);
    const [lighting, setLighting] = useAtom(lightingAtom);
    const [mood, setMood] = useAtom(moodAtom);
    const [scaleAndProportion, setScaleAndProportion] = useAtom(scaleAndProportionAtom);
    const [colorPallete, setColorPallete] = useAtom(colorPalleteAtom);
    const [additionalElements, setAdditionalElements] = useAtom(additionalElementsAtom);
    const aigenerationLayerProgress = useSetAtom(aigenerationLayerProgressAtom);
    const aigenerationLayer = useAtomValue(aigenerationLayerProgressAtom)
    // Dynamically served assisted prompt List
    const [materialList, setMaterialList] = useAtom(materialListAtom);
    const [settingsList, setSettingsList] = useAtom(settingsListAtom);
    const [productList, setProductList] = useAtom(productListAtom);
    const [lightingList, setLightingList] = useAtom(lightingListAtom);
    const [moodList, setMoodList] = useAtom(moodListAtom);
    const [scaleAndProportionList, setScaleAndProportionList] = useAtom(scaleAndProportionListAtom);
    const [colorPalleteList, setColorPalleteList] = useAtom(colorPalleteListAtom);
    const [additionalElementsList, setAdditionalElementsList] = useAtom(additionalElementsListAtom);
    const selectedLayer = useAtomValue(selectedLayerAtom);

    const toast = useToast();
    const {resetPromptHandler} = useResetAtomHandler();

    const productImage = useMemo(()=>{
        let layer = selectedLayer?.getTags()?.includes("product-image") ? selectedLayer : layers.find(layer=>layer?.getTags()?.includes("product-image"));
        if(layer?.getTags()?.includes("non-bg-removed")) return undefined;
        return layer;
    },[layers]);

    const isNonBgRemoved = useMemo(
        ()=>layers.find(layer=>layer?.getTags()?.includes("product-image"))?.getTags()?.includes("non-bg-removed"),
        [layers]
    );

    const uploadProductImage = async (prompt) => {
        if(!prompt) return toast.error("Please enter prompt!",2000);
        let serializedEditor = await activeEditor.save();
        const layer = serializedEditor.layers.find(layer=>layer.id===productImage.id);
        if(!layer) return toast.error("Cannot find Product Image!");
        const {formattedTimestamp} = FormattedTimestamp();
        if(Object.keys(getSegmentPayloadAtom).length)TrackWebappEvent(`${process.env.NEXT_PUBLIC_EVENT_TRACK_TYPE}${EventTrackName._Click_generate_image_ai_studio}`,{...getSegmentPayloadAtom,job_id:activeProject?.job_id, request_time: formattedTimestamp});
        for (let i = 1; i < 7; i++) {
            await new Promise(resolve => setTimeout(resolve, 2000)); 
            aigenerationLayerProgress(10 * i+1);
        }
        const editor = new DMSCanvas("canvas2",activeEditor.dimensions,activeEditor.originalSize);
        await new Promise((res)=>editor.canvas.setBackgroundColor("rgba(0,0,0,0)",res));
        const layerRef = await editor.addLayerFromSerializedData(layer);
        await layerRef.setPropertiesFromSerializedData(layer);
        editor.canvas.renderAll();
        const aspectRatio = activeEditor.originalSize.height / activeEditor.originalSize.width;
        if(aspectRatio > 1) await editor.resize({height: 640, width: 640 / aspectRatio});
        else await editor.resize({height: 640 * aspectRatio, width: 640});
        editor.canvas.renderAll();
        setGenerating(true);
        const base64 = editor.toBase64Url();
        setBase64(base64)
        setUrls(Array(4).fill(0).map(x=>({url:"",loading:true})));
        try{
            aigenerationLayerProgress(90)
            let newUrls = Array(4).fill(0).map(x=>({url:"",loading:true}));
            newUrls = await generateAIImages(prompt, newUrls, productImage, setS3Url, setUrls, setGenerating, aigenerationLayerProgress, activeProject, "",activePage,pagesStates, pages, base64)
            setUrls([...newUrls]);
            setGenerating(false);
            const {formattedTimestamp} = FormattedTimestamp();
            if(Object.keys(getSegmentPayloadAtom).length)TrackWebappEvent(`${process.env.NEXT_PUBLIC_EVENT_TRACK_TYPE}${EventTrackName._generation_done_ai_studio}`,{...getSegmentPayloadAtom,job_id:activeProject?.job_id, response_time: formattedTimestamp});
        }
        catch(e){
            toast.error("Unable to process the Image, Please try again!");
            console.error("error",e )
            setIsAIBackgrounds(false)
            resetPromptHandler()
        }
        finally{
            aigenerationLayerProgress(100);
            setGenerating(false);
        }
    }

    useEffect(()=>{
        const subscription = activeEditor?.layerManager.layers.subscribe(setLayers);
        return () => {
            subscription?.unsubscribe();
        }
    },[activeEditor]);

    const getLatestProductUrl = async()=>{
        aigenerationLayerProgress(10);
        let product_id = null;
        const tags = productImage?.getTags();
        if (tags[1].startsWith("product-image-id-")) {
        product_id = tags[1].replace("product-image-id-", "");
        } else if (tags[1].startsWith("product-asset-id-")) {
        product_id = pagesStates.find(x=>x.page == activePage).imageId;
        } 
        let serializedEditor = await activeEditor.save();
        const editor = new DMSCanvas("AI-PROMPTcanvas2",activeEditor.dimensions,activeEditor.originalSize);
        await new Promise((res)=>editor.canvas.setBackgroundColor("rgba(0,0,0,0)",res));
        const layer = serializedEditor.layers.find(layer=>layer.id===productImage.id);
        const layerRef = await editor.addLayerFromSerializedData(layer);
        await layerRef.setPropertiesFromSerializedData(layer);
        editor.canvas.renderAll();
        const aspectRatio = activeEditor.originalSize.height / activeEditor.originalSize.width;
        if(aspectRatio > 1) await editor.resize({height: 640, width: 640 / aspectRatio});
        else await editor.resize({height: 640 * aspectRatio, width: 640});
        editor.canvas.renderAll();
        const base64 = editor.toBase64Url();
        setBase64(base64)
        const formData = new FormData();
        let needBGRemove = false;
        let imageBlob = await decodeBase64ToBlob(base64);
        try {
            needBGRemove = await needToRemoveBG(base64);
          } catch (error) {
            console.error("Error determining background removal necessity:", error);
            needBGRemove = true; 
          }
        const myFile = new File([imageBlob], 'image.png', {
            type: 'image/png',
        });
        formData.append('file', myFile);
        formData.append("jobOrderId", activeProject?.job_id);
        formData.append("imageId", product_id);
        formData.append("bgRemovedRequired", needBGRemove);
        //@ts-ignore
        const newUrlResponse = await GlobalAPI.uploadBeforeGenerateImage(formData);
        const url = newUrlResponse?.data?.data;     
        return url; 
    }
    
    useEffect(()=>{
        const generatePromptAndAttributes = async () => {
        if(!productImage || urls?.length > 0) return;
        if(!attributePage && !aigenerationLayer)
        {
            //@ts-ignore
            const url = await getLatestProductUrl();
            setPromptGenerating(true);
            setPrompt('');
      
            try {
              const res = await generateAttributesAndPrompt(url);
                 if (res && res.prompt) {
                    setAttributeList(res?.attributesList);
                    setProductList(res?.attributesList?.[0]?.values)
                    setSettingsList(res?.attributesList?.[1]?.values)
                    setMaterialList(res?.attributesList?.[2]?.values)
                    setLightingList(res?.attributesList?.[3]?.values)
                    setColorPalleteList(res?.attributesList?.[4]?.values)
                    setMoodList(res?.attributesList?.[5]?.values)
                    setScaleAndProportionList(res?.attributesList?.[6]?.values)
                    setAdditionalElementsList(res?.attributesList?.[7]?.values)
        
                    //Initialization of assistance
                    setMaterial(res?.attributesList?.[2]?.value)
                    setProduct(res?.attributesList?.[0]?.value)
                    setSettings(res?.attributesList?.[1]?.value)
                    setMaterial(res?.attributesList?.[2]?.value)
                    setLighting(res?.attributesList?.[3]?.value)
                    setColorPallete(res?.attributesList?.[4]?.value)
                    setMood(res?.attributesList?.[5]?.value)
                    setScaleAndProportion(res?.attributesList?.[6]?.value)
                    setAdditionalElements(res?.attributesList?.[7]?.value)
                    setPrompt(res?.prompt);
                    setPromptGenerating(false);
                    uploadProductImage(res?.prompt);
              } else {
                throw new Error("Invalid response");
              }
            } catch (error) {
              resetPromptHandler();
            }
          }
        };
      
        generatePromptAndAttributes();
     
    },[productImage,urls]);
      

    useEffect(()=>{
        if(!prompt || promptGenerating) return;
        const match = prompt.match(pattern);
        if(!match) {
            setAssisted(false);
            return ()=>{};
        }
        const { material, settings, product, lighting, mood, scaleAndProportion, colorPallete, additionalElements } = match.groups;
        setAssisted(true);
        setSettings(settings);
        setProduct(product);
        setLighting(lighting);
        setMood(mood);
        setScaleAndProportion(scaleAndProportion);
        setColorPallete(colorPallete);
        setAdditionalElements(additionalElements);
        setMaterial(material);
            
    },[prompt,promptGenerating]);

    useEffect(()=>{
        if(!prompt) return;
        // if(urls?.length > 1) return;
        const newPrompt = `${product} in ${settings} on ${material} in Lighting ${lighting} with setting color ${colorPallete} and Mood ${mood} and Scale Context ${scaleAndProportion} next to ${additionalElements}`
        setAssisted(true);
        setPrompt(newPrompt);
    },[material, settings, product, lighting, mood, scaleAndProportion, colorPallete, additionalElements])

    return (<>
        {isNonBgRemoved && <RemoveBackgroundAndApply />}

        {productImage && !attributePage && <AIBackgroundResults 
            urls={urls} 
            setGenerating={setGenerating} 
            setUrls={setUrls} 
            setS3Url={setS3Url} 
            s3Url={s3Url} 
            prompt={prompt} 
            productImage={productImage} 
            setPrompt={setPrompt}
            generating={generating}
            setAttributePage={setAttributePage}
            activeProject={activeProject}
            getSegmentPayloadAtom={getSegmentPayloadAtom}
            base64={base64Url}
        />}
        {productImage && attributePage && <PromptGenerator 
            setGenerating={setGenerating} 
            generating={generating}
            setUrls={setUrls} 
            setS3Url={setS3Url} 
            prompt={prompt} 
            assisted={assisted}
            setMaterial={setMaterial}
            setProduct={setProduct}
            setAdditionalElements={setAdditionalElements}
            setColorPalette={setColorPallete}
            setLighting={setLighting}
            setSettings={setSettings}
            setMood={setMood}
            setScaleAndProportion={setScaleAndProportion}
            setPrompt={setPrompt}
            setAssisted={setAssisted}
            materialList={materialList}
            settingsList={settingsList}
            productList={productList}
            lightingList={lightingList}
            moodList={moodList}
            scaleAndProportionList={scaleAndProportionList}
            additionalElementsList={additionalElementsList}
            colorPalleteList={colorPalleteList}
            material={material}
            settings={settings}
            product={product}
            lighting={lighting}
            mood={mood}
            scaleAndProportion={scaleAndProportion}
            colorPalette={colorPallete}
            additionalElements={additionalElements}
            setIsAIBackgrounds={setIsAIBackgrounds}
            setAttributePage={setAttributePage}
            getSegmentPayloadAtom={getSegmentPayloadAtom}
            activeProject={activeProject}
        />}
    </>)
}